// @ts-check
// Menu de comandos (doc 15, seções 7 a 9; contrato 08-contrato-comandos-atalhos): campo "Digite qualquer coisa...",
// chips de filtro por objeto, busca global com debounce de 300 ms em /rest/search e subtítulo de cada resultado
// (telefone, etapa...), busca vazia com registros recentes e favoritos, ações do contexto vindas do registro de
// comandos (seleção, registro aberto, lista e Configurações), automações manuais, "Ir para" com os atalhos G + letra
// e "Criar". Escolher "Mudar etapa" no kanban abre a lista de etapas como subpágina.
import { openRecord as openRecordByPreference } from './open-record.js';
import { html } from 'htm/preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import { findRecordsByIds, searchRecords } from '../../data/rest.js';
import { favoritesState, navigationItemsState, objectsByNameSingular, userObjectsState } from '../../data/metadata-store.js';
import { getAvatarType } from '../../lib/field-metadata.js';
import { filterCommandItems } from '../../lib/command-registry.js';
import { buildRecordSubtitle, groupResultIdsByObject, orderSearchFilterObjects } from '../../lib/search-preview.js';
import { visibleRecentRecords } from '../../lib/recent-records.js';
import { navigate } from '../../app/router.js';
import { commandMenuAutomationTarget, requestManualRun, useManualAutomations } from '../automations/manual-automations.js';
import { routeState } from '../../app/router.js';
import { Avatar } from '../../ui/avatar.js';
import { Icon } from '../../ui/icon.js';
import { Tag } from '../../ui/chip.js';
import { IconButton } from '../../ui/button.js';
import { MenuItem } from '../../ui/dropdown.js';
import { useDebouncedValue } from '../../ui/hooks.js';
import { closeSidePanel, openCreateRecordPanel } from './side-panel-state.js';
import { getCommandLabel, getCurrentCommands, getStageChoices, runCommand } from '../command-menu/command-actions.js';
import { getRecentRecords, verifyRecentRecords } from '../command-menu/command-state.js';
import { hasStageScope } from '../../data/stage-scope.js';
import { goToShortcutsState } from '../command-menu/global-hotkeys.js';
import { PREVIEW_FORMATTERS } from '../command-menu/use-active-record.js';
import { t, translateMetadataLabel } from '../../i18n/index.js';

/**
 * @typedef {{ id: string, group: string, text: string, icon?: string, leftComponent?: any, rightComponent?: any, contextualText?: string,
 *   hotKeys?: string[], accent?: 'default' | 'danger', hasSubMenu?: boolean, run: () => void }} CommandItem
 */

const SEARCH_LIMIT = 20;

/** @param {string} objectNameSingular @param {string} recordId @param {string} label @param {string | null} [imageUrl] */
function recordAvatar(objectNameSingular, recordId, label, imageUrl = null) {
  const objectMetadata = objectsByNameSingular.value.get(objectNameSingular);
  return html`<${Avatar} placeholder=${label} seed=${recordId} size="md" type=${getAvatarType(objectMetadata)} avatarUrl=${imageUrl} />`;
}

/** @param {string} objectNameSingular */
function objectLabelTag(objectNameSingular) {
  const objectMetadata = objectsByNameSingular.value.get(objectNameSingular);
  return objectMetadata ? html`<span class="command-menu-object-label">${translateMetadataLabel(objectMetadata.labelSingular)}</span>` : null;
}

/** @param {string} objectNameSingular @param {string} recordId */
function openRecord(objectNameSingular, recordId) {
  closeSidePanel();
  openRecordByPreference({ objectNameSingular, recordId });
}

export function CommandMenuPage() {
  const [searchText, setSearchText] = useState('');
  const [objectFilter, setObjectFilter] = useState(/** @type {string | null} */ (null));
  const [subPage, setSubPage] = useState(/** @type {{ title: string, items: CommandItem[] } | null} */ (null));
  const debouncedSearchText = useDebouncedValue(searchText.trim(), 300);
  const [results, setResults] = useState(/** @type {any[]} */ ([]));
  const [subtitles, setSubtitles] = useState(/** @type {Map<string, string>} */ (new Map()));
  const [isSearching, setIsSearching] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(0);
  const inputRef = useRef(/** @type {HTMLInputElement | null} */ (null));
  const listRef = useRef(/** @type {HTMLDivElement | null} */ (null));
  const route = routeState.value;
  const automationTarget = commandMenuAutomationTarget({
    recordShow: route?.name === 'recordShow' ? { objectNameSingular: route.params.objectNameSingular, recordId: route.params.recordId } : null,
  });
  const manualAutomations = useManualAutomations(automationTarget.objectNameSingular);
  const shouldSearch = Boolean(debouncedSearchText) || Boolean(objectFilter);

  useEffect(() => {
    inputRef.current?.focus();
  }, [subPage, objectFilter]);

  // papel restrito por etapa: os recentes que foram para uma etapa oculta saem ao abrir o menu
  useEffect(() => {
    if (hasStageScope()) verifyRecentRecords();
  }, []);

  useEffect(() => {
    if (!shouldSearch) {
      setResults([]);
      return undefined;
    }
    let isCancelled = false;
    setIsSearching(true);
    searchRecords(debouncedSearchText, { limit: SEARCH_LIMIT, includedObjectNameSingulars: objectFilter ? [objectFilter] : undefined })
      .then((items) => {
        if (!isCancelled) setResults(items);
      })
      .catch(() => {
        if (!isCancelled) setResults([]);
      })
      .finally(() => {
        if (!isCancelled) setIsSearching(false);
      });
    return () => {
      isCancelled = true;
    };
  }, [debouncedSearchText, objectFilter]);

  // subtítulo de cada resultado: um pedido por objeto, já cortado pelo papel na API
  useEffect(() => {
    if (results.length === 0) return undefined;
    let isCancelled = false;
    const grouped = groupResultIdsByObject(results);
    Promise.all([...grouped].map(async ([objectNameSingular, ids]) => {
      const objectMetadata = objectsByNameSingular.value.get(objectNameSingular);
      if (!objectMetadata) return [];
      const records = await findRecordsByIds(objectMetadata, ids).catch(() => []);
      return records.map((record) => /** @type {[string, string]} */ ([`${objectNameSingular}:${record.id}`, buildRecordSubtitle(objectMetadata, record, PREVIEW_FORMATTERS)]));
    })).then((entries) => {
      if (!isCancelled) setSubtitles(new Map(entries.flat()));
    });
    return () => {
      isCancelled = true;
    };
  }, [results]);

  const readableObjectNames = new Set(userObjectsState.value.map((objectMetadata) => objectMetadata.nameSingular));
  const filterObjects = orderSearchFilterObjects(userObjectsState.value);

  /** @type {CommandItem[]} */
  let items;
  if (subPage) {
    items = filterCommandItems(subPage.items, searchText);
  } else {
    /** @type {CommandItem[]} */
    const resultItems = results.map((result) => ({
      id: `result-${result.objectNameSingular}-${result.recordId}`,
      group: 'results',
      text: result.label || t('record.untitled'),
      contextualText: subtitles.get(`${result.objectNameSingular}:${result.recordId}`) || undefined,
      leftComponent: recordAvatar(result.objectNameSingular, result.recordId, result.label, result.imageUrl ?? null),
      rightComponent: objectLabelTag(result.objectNameSingular),
      run: () => openRecord(result.objectNameSingular, result.recordId),
    }));
    const resultIds = new Set(results.map((result) => result.recordId));
    /** @type {CommandItem[]} */
    const recentItems = debouncedSearchText || searchText.trim()
      ? []
      : visibleRecentRecords(getRecentRecords(), { readableObjectNames, objectFilter, excludedIds: objectFilter ? resultIds : new Set() }).map((recent) => ({
        id: `recent-${recent.objectNameSingular}-${recent.recordId}`,
        group: 'recent',
        text: recent.label || t('record.untitled'),
        contextualText: recent.subtitle || undefined,
        leftComponent: recordAvatar(recent.objectNameSingular, recent.recordId, recent.label),
        rightComponent: objectLabelTag(recent.objectNameSingular),
        run: () => openRecord(recent.objectNameSingular, recent.recordId),
      }));
    if (objectFilter) {
      items = [...recentItems, ...resultItems.filter((item) => !recentItems.some((recent) => recent.id.slice('recent-'.length) === item.id.slice('result-'.length)))];
    } else {
      /** @type {CommandItem[]} */
      const favoriteItems = searchText.trim()
        ? []
        : favoritesState.value.filter((favorite) => favorite.type !== 'FOLDER').map((favorite) => ({
          id: `favorite-${favorite.id}`,
          group: 'favorites',
          text: favorite.label,
          icon: favorite.icon,
          run: () => {
            closeSidePanel();
            navigate(favorite.href);
          },
        }));
      const { commands, runtime } = getCurrentCommands();
      const stageChoices = getStageChoices(runtime.selection);
      /** @type {CommandItem[]} */
      const contextualItems = commands.map((command) => {
        const isStagePicker = command.id === 'selection.changeStage' && Boolean(stageChoices);
        return {
          id: command.id,
          group: command.group,
          text: getCommandLabel(command),
          icon: command.icon,
          accent: command.accent ?? 'default',
          hotKeys: command.hotKeys,
          hasSubMenu: isStagePicker,
          contextualText: command.group === 'selection' && runtime.selection && runtime.selection.count > 1 ? t('table.selectedCount', { count: runtime.selection.count }) : undefined,
          run: () => {
            if (isStagePicker && stageChoices) {
              setSearchText('');
              setSubPage({
                title: getCommandLabel(command),
                items: stageChoices.map((option) => ({
                  id: `stage-${option.value ?? 'none'}`,
                  group: 'stage',
                  text: option.label,
                  leftComponent: html`<${Tag} color=${option.color} text=${option.label} variant=${option.value === null ? 'outline' : 'solid'} />`,
                  run: () => runCommand(command, runtime, { stageValue: option.value }),
                })),
              });
              return;
            }
            runCommand(command, runtime);
          },
        };
      });
      /** @type {CommandItem[]} */
      const automationItems = automationTarget.selection
        ? manualAutomations.map((automation) => ({
          id: `automation-${automation.id}`,
          group: 'automations',
          text: t('automations.manual.runOn', { name: automation.name }),
          contextualText: automationTarget.selection && automationTarget.selection.count > 1 ? t('table.selectedCount', { count: automationTarget.selection.count }) : undefined,
          icon: 'IconHandMove',
          run: () => {
            closeSidePanel();
            if (automationTarget.selection) requestManualRun(automation, automationTarget.selection);
          },
        }))
        : [];
      const hotKeysByHref = new Map(goToShortcutsState.value.map((shortcut) => [shortcut.href, ['G', shortcut.key.toUpperCase()]]));
      /** @type {CommandItem[]} */
      const goToItems = navigationItemsState.value.map((item) => ({
        id: `go-${item.id}`,
        group: 'navigate',
        text: t('commandMenu.goTo', { object: item.label }),
        icon: item.icon,
        hotKeys: hotKeysByHref.get(item.href),
        run: () => {
          closeSidePanel();
          navigate(item.href);
        },
      }));
      // telas sem item no menu (Conversas, Painéis) entram pelos atalhos que o papel pode usar
      const menuHrefs = new Set(navigationItemsState.value.map((item) => item.href));
      for (const shortcut of goToShortcutsState.value) {
        if (menuHrefs.has(shortcut.href) || shortcut.href.startsWith('/settings') || shortcut.href.startsWith('/objects/')) continue;
        goToItems.push({
          id: `go-shortcut-${shortcut.key}`, group: 'navigate', text: t(shortcut.labelKey), icon: 'IconArrowRight', hotKeys: ['G', shortcut.key.toUpperCase()],
          run: () => {
            closeSidePanel();
            navigate(shortcut.href);
          },
        });
      }
      // página das novidades de cada versão (contrato 08-contrato-novidades.md, seção 10)
      goToItems.push({
        id: 'go-release-notes', group: 'navigate', text: t('releaseNotes.command'), icon: 'IconSparkles',
        run: () => {
          closeSidePanel();
          navigate('/release-notes');
        },
      });
      /** @type {CommandItem[]} */
      const createItems = userObjectsState.value.map((objectMetadata) => ({
        id: `create-${objectMetadata.id}`,
        group: 'create',
        text: t('commandMenu.create', { object: translateMetadataLabel(objectMetadata.labelSingular) }),
        icon: 'IconPlus',
        run: () =>
          openCreateRecordPanel({
            objectNameSingular: objectMetadata.nameSingular,
            onCreated: (record) => {
              closeSidePanel();
              navigate(`/object/${objectMetadata.nameSingular}/${record.id}`);
            },
          }),
      }));
      const actionItems = filterCommandItems([...favoriteItems, ...contextualItems, ...automationItems, ...goToItems, ...createItems], searchText);
      items = [...resultItems, ...recentItems, ...actionItems];
    }
  }

  const groups = subPage
    ? [{ key: 'stage', label: subPage.title }]
    : [
      { key: 'results', label: t('commandMenu.results') },
      { key: 'recent', label: t('commands.menu.recent') },
      { key: 'favorites', label: t('commands.menu.favorites') },
      { key: 'selection', label: t('commands.menu.selection') },
      { key: 'record', label: t('commands.menu.record') },
      { key: 'conversation', label: t('commands.menu.conversation') },
      { key: 'automations', label: t('automations.manual.menuGroup') },
      { key: 'list', label: t('commands.menu.list') },
      { key: 'navigate', label: t('commandMenu.navigate') },
      { key: 'settings', label: t('commands.menu.settings') },
      { key: 'create', label: t('commandMenu.createGroup') },
    ];

  useEffect(() => {
    setFocusedIndex(0);
  }, [items.length, debouncedSearchText, subPage, objectFilter]);

  useEffect(() => {
    listRef.current?.querySelector('[data-focused="true"]')?.scrollIntoView({ block: 'nearest' });
  }, [focusedIndex]);

  const goBack = () => {
    if (subPage) setSubPage(null);
    else if (objectFilter) setObjectFilter(null);
    else return false;
    return true;
  };

  /** @param {KeyboardEvent} event */
  const handleKeyDown = (event) => {
    if (event.isComposing) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setFocusedIndex((index) => (items.length ? (index + 1) % items.length : 0));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setFocusedIndex((index) => (items.length ? (index - 1 + items.length) % items.length : 0));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      items[focusedIndex]?.run();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      if (searchText) setSearchText('');
      else if (!goBack()) closeSidePanel();
    } else if (event.key === 'Backspace' && !searchText) {
      if (goBack()) event.preventDefault();
    }
  };

  return html`<div class="side-panel-page">
    <div class="side-panel-top-bar">
      <div class="side-panel-top-bar-left">
        ${subPage
          ? html`<${IconButton} icon="IconChevronLeft" size="small" variant="tertiary" ariaLabel=${t('common.back')} onClick=${() => setSubPage(null)} />`
          : html`<${Icon} name="IconSearch" size=${16} className="side-panel-search-icon" />`}
        <input
          ref=${inputRef}
          class="side-panel-search-input"
          placeholder=${t('commandMenu.placeholder')}
          value=${searchText}
          aria-label=${t('commandMenu.placeholder')}
          onInput=${(event) => setSearchText(event.currentTarget.value)}
          onKeyDown=${handleKeyDown}
        />
      </div>
      <${IconButton} icon="IconX" size="small" variant="tertiary" ariaLabel=${t('sidePanel.close')} onClick=${closeSidePanel} />
    </div>
    ${subPage
      ? null
      : html`<div class="command-menu-filters" role="group" aria-label=${t('commands.menu.filterByObject')}>
          <button type="button" class="command-menu-filter-chip" data-active=${objectFilter === null ? 'true' : undefined} aria-pressed=${objectFilter === null}
            onClick=${() => setObjectFilter(null)}>${t('commands.menu.allObjects')}</button>
          ${filterObjects.map((objectMetadata) => html`<button
            key=${objectMetadata.nameSingular}
            type="button"
            class="command-menu-filter-chip"
            data-testid=${`command-filter-${objectMetadata.nameSingular}`}
            data-active=${objectFilter === objectMetadata.nameSingular ? 'true' : undefined}
            aria-pressed=${objectFilter === objectMetadata.nameSingular}
            onClick=${() => setObjectFilter(objectFilter === objectMetadata.nameSingular ? null : objectMetadata.nameSingular)}
          >
            <${Icon} name=${objectMetadata.icon ?? 'IconCube'} size=${12} />
            ${translateMetadataLabel(objectMetadata.labelPlural)}
          </button>`)}
        </div>`}
    <div class="side-panel-content command-menu-list" ref=${listRef}>
      ${groups.map((group) => {
        const groupItems = items.filter((item) => item.group === group.key);
        if (groupItems.length === 0) return null;
        return html`<div class="command-menu-group" key=${group.key} data-group=${group.key}>
          <div class="command-menu-group-label">${group.label}</div>
          ${groupItems.map((item) => {
            const index = items.indexOf(item);
            return html`<${MenuItem}
              key=${item.id}
              text=${item.text}
              icon=${item.icon}
              leftComponent=${item.leftComponent}
              rightComponent=${item.rightComponent}
              contextualText=${item.contextualText}
              hotKeys=${item.hotKeys}
              accent=${item.accent ?? 'default'}
              hasSubMenu=${item.hasSubMenu}
              focused=${index === focusedIndex}
              onClick=${item.run}
            />`;
          })}
        </div>`;
      })}
      ${items.length === 0 && !isSearching ? html`<div class="command-menu-empty">${t('common.noResults')}</div>` : null}
      ${isSearching && results.length === 0 ? html`<div class="command-menu-empty">${t('commandMenu.searching')}</div>` : null}
    </div>
  </div>`;
}
